/**
 * TelegramEmbed — the "Communications Uplink" section.
 *
 * Visually the v2 join screen, kept intact (corner brackets, scanning line, the
 * "The transmissions have already died" closer). What changed is that the status
 * row now reflects the real relay instead of always claiming "SECURE CHANNEL":
 * the backend tells us whether the bot is wired up and polling, and whether
 * replies will come back to this visitor.
 *
 * Once the relay is live the join screen is replaced by the real transcript of
 * the group's General topic (TELEGRAM_HISTORY_THREAD_ID, served by
 * GET /api/telegram/history), so visitors can read the room before they speak.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, MessageCircle, Send } from 'lucide-react';
import { useComms } from '@/lib/CommsContext';
import { TELEGRAM_URL } from '@/lib/config';

function StatusRow({ relayStatus }) {
  const { enabled, pollerRunning, checked } = relayStatus;

  if (!checked) {
    return (
      <span className="flex items-center gap-2 font-mono text-xs text-[color:var(--phosphor-60)]">
        <span className="dmr-led dmr-led--busy" />
        PROBING CHANNEL
      </span>
    );
  }

  if (!enabled) {
    return (
      <span className="flex items-center gap-2 font-mono text-xs text-[#404040]">
        <span className="dmr-led dmr-led--idle" />
        RELAY OFFLINE · JOIN TO TALK
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2 font-mono text-xs text-[color:var(--phosphor)]">
      <span className="dmr-led" />
      {pollerRunning ? 'LIVE RELAY · REPLIES ROUTED BACK' : 'SECURE CHANNEL'}
    </span>
  );
}

/** Local HH:MM for a transcript row; empty string when the stamp is unusable. */
function stampFor(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * The live transcript of the group's General topic. Newest last, auto-scrolled,
 * and it never shows a Telegram message id — only who said what and when.
 */
function Transcript({ messages, username, threadId }) {
  const scrollerRef = useRef(null);

  useEffect(() => {
    const node = scrollerRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  if (!messages.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <span className="dmr-led dmr-led--busy" aria-hidden="true" />
        <p className="font-mono text-sm text-[color:var(--phosphor-60)]">
          listening to the channel…
        </p>
        <p className="dmr-hint">
          transmissions from the group appear here as they arrive
          {threadId ? ` (topic ${threadId})` : ''}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      className="dmr-scroll dmr-scroll--faded h-full w-full px-4 py-3"
      data-testid="telegram-transcript"
      aria-live="polite"
      aria-label="Recent group transmissions"
    >
      {messages.map((message) => {
        const self = !message.isBot && username && message.senderName?.includes(username);
        return (
          <div
            key={message.messageId ?? `${message.timestamp}-${message.text}`}
            className={`dmr-transcript__row${self ? ' dmr-transcript__row--self' : ''}`}
          >
            <span className="dmr-transcript__time">{stampFor(message.timestamp)}</span>
            <span className="dmr-transcript__who" title={message.senderName}>
              {message.isBot ? '◈ moony' : message.senderName}
            </span>
            <span className="dmr-transcript__text">{message.text}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function TelegramEmbed() {
  const { relayStatus, unreadReplies, history, username } = useComms();

  // Only replace the join screen with the transcript when there is something
  // to read: an unconfigured relay still shows the invitation.
  const showTranscript = useMemo(
    () => relayStatus.enabled && history.messages.length > 0,
    [relayStatus.enabled, history.messages.length],
  );

  return (
    <section
      id="telegram-section"
      className="dmr-section bg-[#050505]"
      aria-label="Telegram community uplink"
    >
      <div className="w-full max-w-4xl">
        <motion.div
          className="mb-8 text-center"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-2 flex items-center justify-center gap-2">
            <MessageCircle className="h-6 w-6 text-[color:var(--phosphor)]" />
            <h2 className="dmr-heading text-2xl md:text-4xl">Communications Uplink</h2>
          </div>
          <p className="text-sm text-[color:var(--phosphor-60)]">
            {showTranscript ? 'DeadMoon Archives • General topic' : 'DeadMoon Archives • Telegram'}
            {unreadReplies > 0 && ` • ${unreadReplies} new`}
          </p>
        </motion.div>

        <motion.div
          className="dmr-panel overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          data-testid="telegram-embed"
        >
          {/* Header bar */}
          <div className="flex items-center justify-between gap-3 border-b border-[color:var(--edge)] bg-[#0a0a0a] px-4 py-3">
            <StatusRow relayStatus={relayStatus} />
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex shrink-0 items-center gap-1 text-xs text-[color:var(--phosphor-60)] transition-colors hover:text-[color:var(--phosphor)]"
            >
              <span>Open in Telegram</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {/* Panel body: the live transcript of the General topic once the
              relay has something to show, otherwise the v2 join screen. */}
          {showTranscript ? (
            <div
              className="relative h-[46vh] min-h-[18rem] w-full overflow-hidden bg-[color:var(--panel)] md:h-[60vh]"
              data-testid="telegram-transcript-panel"
            >
              <Transcript
                messages={history.messages}
                username={username}
                threadId={history.threadId}
              />
            </div>
          ) : (
          <div className="relative flex h-[46vh] min-h-[18rem] w-full items-center justify-center overflow-hidden bg-[color:var(--panel)] p-6 md:h-[60vh]">
            <div
              className="pointer-events-none absolute inset-0 opacity-5"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(0,255,65,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,65,0.3) 1px, transparent 1px)',
                backgroundSize: '30px 30px',
              }}
              aria-hidden="true"
            />

            <motion.div
              className="relative mb-6 md:mb-8"
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="relative mx-auto flex h-36 w-36 items-center justify-center overflow-hidden rounded-lg border-2 border-[color:var(--phosphor-20)] bg-[#0a0a0a] sm:h-48 sm:w-48 md:h-56 md:w-56">
                {/* Corner brackets */}
                <span className="absolute left-2 top-2 h-3 w-3 border-l-2 border-t-2 border-[color:var(--phosphor)] sm:h-4 sm:w-4" />
                <span className="absolute right-2 top-2 h-3 w-3 border-r-2 border-t-2 border-[color:var(--phosphor)] sm:h-4 sm:w-4" />
                <span className="absolute bottom-2 left-2 h-3 w-3 border-b-2 border-l-2 border-[color:var(--phosphor)] sm:h-4 sm:w-4" />
                <span className="absolute bottom-2 right-2 h-3 w-3 border-b-2 border-r-2 border-[color:var(--phosphor)] sm:h-4 sm:w-4" />

                <div className="p-2 text-center">
                  <Send className="mx-auto mb-2 h-16 w-16 text-[color:var(--phosphor-35)] sm:mb-4 sm:h-20 sm:w-20 md:h-24 md:w-24" />
                  <p className="text-[10px] text-[color:var(--phosphor-35)] sm:text-xs">
                    DeadMoon Archives
                  </p>
                </div>
              </div>

              {/* Scanning line */}
              <motion.div
                className="pointer-events-none absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-[color:var(--phosphor-60)] to-transparent"
                animate={{ top: ['0%', '100%', '0%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                aria-hidden="true"
              />
            </motion.div>

            <div className="relative z-10 max-w-sm text-center">
              <h3 className="mb-4 font-mono text-lg text-[color:var(--phosphor)]">
                Join DeadMoon Archives
              </h3>
              <p className="mb-6 text-sm text-[color:var(--bone)]/70">
                {relayStatus.enabled
                  ? 'Type anything in the bar below and it lands in the group. Reply to it there and it comes back to you here.'
                  : 'Connect with fellow listeners in the void. Share music, thoughts, and transmissions from beyond.'}
              </p>

              <a
                href={TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="dmr-button dmr-button--solid !px-6 !py-3 !text-xs"
                data-testid="telegram-join"
              >
                <Send className="h-4 w-4" />
                <span>Open in Telegram</span>
              </a>

              <p className="dmr-hint mt-4">t.me/DeadMoonArchives</p>
            </div>
          </div>
          )}
        </motion.div>

        <motion.p
          className="dmr-hint mt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          The transmissions have already died
        </motion.p>
      </div>
    </section>
  );
}
